import { Injectable, EventEmitter } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface LoaderOptions {
  /** No mostrar el loader hasta que pasen N ms (evita parpadeo en acciones rápidas) */
  delayShowMs?: number; // default: 0
  /** Una vez visible, mantenerlo al menos N ms */
  minShowMs?: number; // default: 300
  /** Seguridad: ocultar y resetear si algo quedó colgado */
  hardTimeoutMs?: number; // default: 60_000
}

@Injectable({ providedIn: "root" })
export class LoaderService {
  /** ======== API LEGADA (compat) ======== */
  /** Igual que antes: algunos componentes se subscriben a este EventEmitter */
  status = new EventEmitter<boolean>();

  /** ======== API NUEVA ======== */
  private loaderSubject = new BehaviorSubject<boolean>(false);
  /** Usa esto en código nuevo (async pipe, etc.) */
  status$: Observable<boolean> = this.loaderSubject.asObservable();

  /** ======== Internos ======== */
  private activeCount = 0;
  private showTimer?: any;
  private hideTimer?: any;
  private hardTimer?: any;
  private lastShowAt = 0;

  private opts: Required<LoaderOptions> = {
    delayShowMs: 0,
    minShowMs: 300,
    hardTimeoutMs: 60_000,
  };

  /** Config global opcional */
  configure(opts: LoaderOptions) {
    this.opts = { ...this.opts, ...opts };
  }

  /** Mantengo la firma antigua: start(msg?: any) */
  start(_msg?: any): symbol {
    const token = Symbol("loader");
    this.activeCount++;

    // cancelar ocultado pendiente si llega otra operación
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }

    // si ya está visible, solo asegurar hard-timeout
    if (this.isVisible()) {
      this.ensureHardTimeout();
      return token;
    }

    // programar aparición (delay opcional)
    if (!this.showTimer) {
      this.showTimer = setTimeout(() => {
        this.showTimer = undefined;
        if (this.activeCount > 0) {
          this.lastShowAt = Date.now();
          this.setVisible(true);
          this.ensureHardTimeout();
        }
      }, this.opts.delayShowMs);
    }

    return token;
  }

  /** Mantengo la firma antigua: stop(time?: number, msg?: any) */
  stop(time: number = 0, _msg?: any): void {
    // Permito un pequeño “tiempo extra” solicitado explícitamente
    if (time > 0) {
      setTimeout(() => this.stop(0, _msg), time);
      return;
    }

    if (this.activeCount > 0) this.activeCount--;

    // aún quedan operaciones activas → seguir visible
    if (this.activeCount > 0) return;

    const elapsed = Date.now() - this.lastShowAt;
    const remaining = Math.max(this.opts.minShowMs - elapsed, 0);

    // si todavía no apareció (delayShowMs), cancelo el show
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = undefined;
    }

    // programar ocultado respetando minShowMs
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.hideTimer = undefined;
      this.setVisible(false);
      this.clearHardTimeout();
      this.lastShowAt = 0;
    }, remaining);
  }

  /** Utilidades nuevas por si las quieres usar */
  reset(): void {
    this.activeCount = 0;
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = undefined;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = undefined;
    }
    this.clearHardTimeout();
    this.setVisible(false);
    this.lastShowAt = 0;
  }

  async wrapPromise<T>(p: Promise<T>): Promise<T> {
    const t = this.start();
    try {
      return await p;
    } finally {
      this.stop(0);
    }
  }

  // ======== privados ========
  private setVisible(v: boolean) {
    // emite en **ambos** canales (legado + nuevo)
    this.loaderSubject.next(v);
    this.status.emit(v);
  }

  private isVisible(): boolean {
    return this.loaderSubject.getValue();
  }

  private ensureHardTimeout() {
    this.clearHardTimeout();
    this.hardTimer = setTimeout(() => this.reset(), this.opts.hardTimeoutMs);
  }

  private clearHardTimeout() {
    if (this.hardTimer) {
      clearTimeout(this.hardTimer);
      this.hardTimer = undefined;
    }
  }
}
